<script setup lang="ts">
import { storeToRefs } from 'pinia'
import clone from 'lodash/clone'
import cloneDeep from 'lodash/cloneDeep'
import { computed, onMounted, ref, watch } from 'vue'
import {
  Dialog,
  DialogTitle,
  DialogPanel,
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { PencilSquareIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { ArrowLeftIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'
import { useI18n } from 'vue-i18n'
import { DateTime } from 'luxon'
import moment from 'moment/moment'
import { useMainStore } from '~/store/MainStore'
import { useOrderStore } from '~/store/OrderStore'
import { useUserStore } from '~/store/UserStore'
import type {
  BpLocation, DeliveryAddress,
  SimpleNumberSelectOption,
} from '~/definitions/AllDefinitions'
import {
  copyAddressFromOrder,
  getFormattedAddress,
  isLast, updateOrderFromAddress,
} from '~/classes/Utils'
import DeliveryAddressItem from '~/components/DeliveryMethod/DeliveryAddressItem.vue'
import GooglePlacesAutoComplete from '~/components/Location/GooglePlacesAutoComplete.vue'
import { roundToNearestFifteenMinutes } from '~/classes/HourUtils'
import { DELIVERY_METHODS } from '~/definitions/enums'

interface DeliveryMethodSelections {
  locationId: number
  deliveryAddress: DeliveryAddress | null
  deliveryMethod: string
  curbsidePickup: boolean
  scheduledDay: SimpleNumberSelectOption
  scheduledTime: SimpleNumberSelectOption
}
interface DeliveryOptions {
  deliveryAddress: DeliveryAddress | null
  loading: boolean
  locationId: number
  locations: BpLocation[]
}
interface PickupOptions {
  locationId: number
}
const defaultSelections = {
  locationId: 0,
  deliveryAddress: null,
  deliveryMethod: '',
  curbsidePickup: false,
  scheduledDay: { label: '', value: 0 },
  scheduledTime: { label: '', value: 0 },
}
const props = defineProps<{
  open: boolean
  enableTimeSelector: boolean
}>()
// data
const { t } = useI18n()
const emit = defineEmits(['close', 'update:modelValue'])
const mainStore = useMainStore()
const userStore = useUserStore()
const orderStore = useOrderStore()
const { currentUser } = storeToRefs(userStore)
const { currentDeliveryMethods, currentLocations, currentLocation, timeZone } = storeToRefs(mainStore)
const { order } = storeToRefs(orderStore)
const addressItems = ref([] as DeliveryAddress[])
const currentStep = ref(0)
const deliveryOptions = ref({
  deliveryAddress: null,
  loading: false,
  locationId: 0,
  locations: [],
} as DeliveryOptions)
const isValidDeliveryAddress = ref(false)
const loading = ref(false)
const pickupOptions = ref({
  locationId: 0,
} as PickupOptions)
const selections = ref({ ...defaultSelections } as DeliveryMethodSelections)
const steps = ref(['PICKUP_DELIVERY', 'SELECT_PICKUP_LOCATION', 'FIND_STORE'] as string[])
const error = ref('')
// Computed Properties
const formattedPickupAddress = computed(() => {
  if (!selections.value.locationId || !(currentLocations.value && currentLocations.value.length)) {
    return ''
  }
  const items = currentLocations.value.filter((location) => {
    return location.id === selections.value.locationId
  })
  return items.length ? items[0].formattedAddress : ''
})
const deliveryName = computed(() => {
  return selections.value.deliveryAddress &&
    (selections.value.deliveryAddress.firstName || selections.value.deliveryAddress.lastName)
    ? selections.value.deliveryAddress.firstName && selections.value.deliveryAddress.lastName
      ? `${selections.value.deliveryAddress.firstName} ${selections.value.deliveryAddress.lastName}`
      : selections.value.deliveryAddress.firstName
        ? selections.value.deliveryAddress.firstName
        : selections.value.deliveryAddress.lastName
    : ''
})
const formattedDeliveryAddress = computed(() => {
  return selections.value.deliveryAddress
    ? deliveryName.value
      ? `${deliveryName.value} @ ${getFormattedAddress(selections.value.deliveryAddress)}`
      : getFormattedAddress(selections.value.deliveryAddress)
    : ''
})

const scheduledTimeLabel = computed(() => {
  if (!selections.value.scheduledTime?.value || !intervals.value.length) {
    return '---'
  }
  if (isToday.value && intervals.value[0].value === selections.value.scheduledTime.value) {
    return t('ASAP_DELIVERY_X', { value: (currentLocation as Ref<BpLocation>).value.prepTime })
  }
  return selections.value.scheduledTime.label
})
const days = computed(() => {
  if (!currentLocation.value ||
    !(currentLocation.value && currentLocation.value.intervals) ||
    Object.keys(currentLocation.value.intervals).length === 1
  ) {
    return null
  }
  return Object.keys(currentLocation.value.intervals).map(
    (value) => {
      return {
        label: value,
        value: currentLocation.value!.intervals![value].intervalValue,
      }
    },
  )
})
const intervals = computed(() => {
  if (!props.open || !(currentLocation.value && currentLocation.value.intervals)) {
    return []
  }
  const nowWithPrep = DateTime
    .utc()
    .setZone((timeZone.value || 'America/Los_Angeles'))
    .plus({ minutes: currentLocation.value.prepTime })
    .set({
      second: 0,
      millisecond: 0,
    })
  const selectedDay = selections.value.scheduledDay.value || moment().startOf('day').valueOf()
  let dayIntervals = []
  for (const dayIntervalsKey in currentLocation.value.intervals) {
    if (currentLocation.value.intervals[dayIntervalsKey].intervalValue === selectedDay) {
      dayIntervals = currentLocation.value.intervals[dayIntervalsKey].intervals
      break
    }
  }
  const intervals = []
  if (!(dayIntervals && dayIntervals.length)) {
    return dayIntervals
  }
  for (let i = 0; i < dayIntervals.length; i++) {
    const interval = dayIntervals[i]
    if (interval.label === 'ASAP') {
      intervals.push(interval)
    } else if (interval.value > nowWithPrep) {
      intervals.push(interval)
    }
  }
  return intervals
})
// methods
function checkDeliveryMethods() {
  if (currentDeliveryMethods.value.length === 1) {
    const deliveryMethod = clone(currentDeliveryMethods.value[0].value)
    selections.value.deliveryMethod = deliveryMethod
    order.value.deliveryMethod = deliveryMethod
    if (!props.enableTimeSelector) {
      closeDialog()
    }
  } else if (!currentDeliveryMethods.value.length) {
    if (!props.enableTimeSelector) {
      closeDialog()
    }
  } else if (order.value.deliveryMethod) {
    selections.value.deliveryMethod = clone(order.value.deliveryMethod)
  }
}
function changeDeliveryMethod(method: string) {
  selections.value.deliveryMethod = clone(method)
  if (
    method === DELIVERY_METHODS.DELIVERY &&
    !(order.value.deliveryAddress && order.value.deliveryAddress.addressOne)) {
    editDeliveryLocation()
  } else if (method === DELIVERY_METHODS.PICKUP && !selections.value.locationId) {
    editPickupLocation()
  }
}
async function checkValidDeliveryAddress() {
  if (selections.value.deliveryAddress && selections.value.locationId) {
    isValidDeliveryAddress.value = await mainStore.checkLocationDeliverability(
      selections.value.locationId,
      selections.value.deliveryAddress.latitude as number,
      selections.value.deliveryAddress.longitude as number,
    )
  } else {
    isValidDeliveryAddress.value = false
  }
}
function closeDialog() {
  emit('close')
}
async function confirmDeliveryInformation() {
  selections.value.deliveryAddress = cloneDeep(deliveryOptions.value.deliveryAddress)
  selections.value.locationId = clone(deliveryOptions.value.locationId)
  isValidDeliveryAddress.value = true
  await goBack()
  await save()
}
async function confirmPickupLocation() {
  selections.value.locationId = clone(pickupOptions.value.locationId)
  await goBack()
  await save()
}
function disableConfirmDeliveryInformation() {
  return !(deliveryOptions.value.locationId && deliveryOptions.value.deliveryAddress)
}
function disableSave() {
  if (props.enableTimeSelector && !selections.value.scheduledTime?.value) {
    return true
  }
  if (!selections.value.deliveryMethod) {
    return true
  } else if (selections.value.deliveryMethod === DELIVERY_METHODS.PICKUP) {
    return selections.value.locationId <= 0
  } else {
    return !validSelectedTime.value ||
      !(selections.value.deliveryAddress && selections.value.locationId && isValidDeliveryAddress.value)
  }
}
const validSelectedTime = computed(() => {
  if (!(selections.value.scheduledTime && selections.value.scheduledTime.value)) {
    return false
  }
  return selections.value.scheduledTime.label === 'ASAP' ||
    intervals.value
      .some((interval: SimpleNumberSelectOption) => interval.value === selections.value.scheduledTime.value)
})
function editDeliveryLocation() {
  resetDeliveryOptions()
  currentStep.value = 2
}
function editPickupLocation() {
  if (selections.value.locationId) {
    pickupOptions.value.locationId = clone(selections.value.locationId)
  } else {
    pickupOptions.value.locationId = 0
  }
  currentStep.value = 1
}
async function getAddresses(skipValidation = false) {
  let copiedAddress = null as DeliveryAddress | null
  if (order.value.latitude && order.value.longitude) {
    copiedAddress = copyAddressFromOrder(order.value)
    selections.value.deliveryAddress = cloneDeep(copiedAddress)
  }
  if (currentUser.value) {
    addressItems.value.length = 0
    const res = await userStore.getAddresses()
    if (res.success) {
      const items = cloneDeep(res.data) as any
      let counter = 0
      items.forEach((item: DeliveryAddress) => {
        if (!item.id) {
          item.id = clone(counter)
          counter++
        }
        addressItems.value.push(item)
      })
    }
  } else if (copiedAddress) {
    addressItems.value.push(copiedAddress)
  }
  if (!skipValidation) {
    if (selections.value.deliveryMethod === DELIVERY_METHODS.DELIVERY && !selections.value.deliveryAddress) {
      editDeliveryLocation()
    } else if (selections.value.deliveryMethod === DELIVERY_METHODS.PICKUP && !selections.value.locationId) {
      editPickupLocation()
    }
    await checkValidDeliveryAddress()
  }
}
async function goBack() {
  currentStep.value = 0
  if (selections.value.deliveryMethod === DELIVERY_METHODS.DELIVERY) {
    await checkValidDeliveryAddress()
  }
}
function handleDayChange(e: any) {
  emit('update:modelValue', e)
  selections.value.scheduledDay = e
  selections.value.scheduledTime = intervals.value[0]
}
const isToday = computed(() => {
  return selections.value.scheduledDay && selections.value.scheduledDay.value === moment().startOf('day').valueOf()
})
function locationName(location: BpLocation) {
  if (currentLocations.value.length > 1) {
    return location.name
  } else {
    return ''
  }
}
async function save() {
  loading.value = true
  if (selections.value.deliveryAddress) {
    updateOrderFromAddress(selections.value.deliveryAddress, order.value)
  }
  order.value.deliveryMethod = clone(selections.value.deliveryMethod)
  if (selections.value.locationId) {
    if (!currentLocation.value || (selections.value.locationId !== currentLocation.value.id)) {
      await mainStore.getLocation(selections.value.locationId)
    }
  }
  loading.value = false
  if (props.enableTimeSelector) {
    orderStore.setCurbsidePickup(selections.value.curbsidePickup)
    saveScheduledDayAndTime({ day: selections.value.scheduledDay, time: selections.value.scheduledTime })
  }
  emit('close', selections.value)
}
function saveScheduledDayAndTime(payload: { day?: SimpleNumberSelectOption, time?: SimpleNumberSelectOption }) {
  if (payload.day) {
    orderStore.setScheduledDay(payload.day)
  }
  if (payload.time) {
    orderStore.setScheduledTime(payload.time)
  }
}
function init(skipValidation = false) {
  if (currentLocations.value.length > 1) {
    steps.value[2] = 'FIND_STORE'
  } else {
    steps.value[2] = 'SELECT_DELIVERY_ADDRESS'
  }
  currentStep.value = 0
  if (currentLocation.value && currentLocation.value.id) {
    selections.value.locationId = clone(currentLocation.value.id)
  }
  checkDeliveryMethods()
  getAddresses(skipValidation)
  if (order.value.scheduledDay && order.value.scheduledDay.value) {
    // previous selected date has elapsed. Update to ASAP
    if (moment(order.value.scheduledDay.value).isBefore(moment().startOf('day'))) {
      selections.value.scheduledDay = { label: 'TODAY', value: moment().startOf('day').valueOf() }
      saveScheduledDayAndTime({ day: selections.value.scheduledDay })
    } else {
      selections.value.scheduledDay = order.value.scheduledDay
    }
  } else {
    selections.value.scheduledDay = { label: 'TODAY', value: moment().startOf('day').valueOf() }
    saveScheduledDayAndTime({ day: selections.value.scheduledDay })
  }
  if (order.value.scheduledTime && (order.value.scheduledTime as SimpleNumberSelectOption).value) {
    const selected = intervals.value.find((interval: SimpleNumberSelectOption) => {
      return order.value.scheduledTime &&
        (interval.value === (order.value.scheduledTime as SimpleNumberSelectOption).value)
    })
    if (selected) {
      if (selected.label === 'ASAP') {
        selections.value.scheduledTime = {
          label: t(selected.label),
          value: selected.value,
        }
      } else {
        selections.value.scheduledTime = clone(selected)
      }
    } else {
      selections.value.scheduledTime = intervals.value[0] || { label: 'ASAP', value: roundToNearestFifteenMinutes() }
      saveScheduledDayAndTime({ time: selections.value.scheduledTime })
    }
  } else {
    selections.value.scheduledTime = intervals.value[0] || { label: 'ASAP', value: roundToNearestFifteenMinutes() }
    saveScheduledDayAndTime({ time: selections.value.scheduledTime })
  }
}
async function handleSelectDeliveryAddress(details: any) {
  deliveryOptions.value.loading = true
  const address = cloneDeep(details) as DeliveryAddress
  address.id = 0
  if (currentUser.value) {
    address.firstName = clone(currentUser.value.firstName)
    address.lastName = clone(currentUser.value.lastName)
    address.email = clone(currentUser.value.email)
    address.phoneFormatted = clone(currentUser.value.phoneFormatted)
    address.phoneUnformatted = clone(currentUser.value.phoneUnformatted)
    address.phoneCountry = clone(currentUser.value.phoneCountry)
    address.phoneType = clone(currentUser.value.phoneType)
  }
  deliveryOptions.value.deliveryAddress = cloneDeep(address)
  await getDeliverableLocations()
}

async function getDeliverableLocations() {
  deliveryOptions.value.loading = true
  deliveryOptions.value.locations.length = 0
  if (deliveryOptions.value.deliveryAddress) {
    const { latitude, longitude } = deliveryOptions.value.deliveryAddress
    for (let i = 0; i < currentLocations.value.length; i++) {
      const isDeliverable = await mainStore.checkLocationDeliverability(
        currentLocations.value[i].id,
        latitude as number,
        longitude as number,
      )
      if (isDeliverable) {
        deliveryOptions.value.locations.push(cloneDeep(currentLocations.value[i]))
      }
    }
  }
  if (currentLocations.value.length === 1 && deliveryOptions.value.locations.length) {
    deliveryOptions.value.locationId = clone(deliveryOptions.value.locations[0].id)
    await confirmDeliveryInformation()
  }
  deliveryOptions.value.loading = false
}
function resetDeliveryOptions() {
  deliveryOptions.value.deliveryAddress = null
  deliveryOptions.value.loading = false
  deliveryOptions.value.locationId = 0
  deliveryOptions.value.locations.length = 0
}
async function selectPickup(changeMethod: boolean) {
  selections.value.deliveryMethod = DELIVERY_METHODS.PICKUP
  currentStep.value = 0
  if (changeMethod) {
    await save()
  }
}
onMounted(() => {
  if (props.open) {
    init(true)
  }
})
watch(() => props.open, () => {
  if (props.open) {
    init()
  } else {
    selections.value = { ...defaultSelections }
  }
})
</script>

<template>
  <TransitionRoot
    as="template"
    :show="open"
  >
    <Dialog
      as="div"
      class="relative z-50"
      @close="closeDialog"
    >
      <TransitionChild
        as="template"
        enter="ease-in-out duration-500"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in-out duration-500"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-neutral-800/40 transition-opacity" />
      </TransitionChild>
      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div
            class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10"
          >
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
            >
              <DialogPanel class="pointer-events-auto w-screen max-w-sm">
                <div
                  class="flex h-full flex-col overflow-y-scroll bg-tw-1 py-6 shadow-xl"
                >
                  <div class="px-4 pb-4 sm:px-6 border-b border-tw-2">
                    <div class="flex items-start justify-between">
                      <DialogTitle
                        class="text-base font-semibold leading-6 text-tw-1"
                      >
                        {{ t(steps[currentStep]) }}
                      </DialogTitle>
                      <div class="ml-3 flex h-7 items-center">
                        <button
                          type="button"
                          class="tw-slide-over-close-button"
                          @click="closeDialog()"
                        >
                          <span class="absolute -inset-2.5" />
                          <span class="sr-only">{{ t('CLOSE') }}</span>
                          <XMarkIcon
                            class="h-6 w-6"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="relative mt-4 flex-1 px-4 sm:px-6">
                    <div
                      v-if="currentStep === 0"
                      class="flex flex-col place-content-between h-full w-full"
                    >
                      <div class="overflow-y-scroll w-full">
                        <div
                          v-if="currentDeliveryMethods.length > 1"
                          class="w-full rounded-md mb-5"
                        >
                          <div class="grid grid-cols-2">
                            <button
                              v-for="(method, methodIdx) in currentDeliveryMethods"
                              :key="method.value"
                              type="button"
                              :class="{
                                'rounded-l-md border-l': methodIdx === 0,
                                'rounded-r-md border-r': methodIdx === 1,
                                'active-button-item': method.value === selections.deliveryMethod,
                                'inactive-button-item': method.value !== selections.deliveryMethod,
                              }"
                              @click="changeDeliveryMethod(method.value)"
                            >
                              {{ t(method.label) }}
                            </button>
                          </div>
                        </div>
                        <div
                          v-if="selections.deliveryMethod === DELIVERY_METHODS.PICKUP"
                          class="w-full"
                        >
                          <div class="w-full flex flex-row justify-between text-sm text-tw-1">
                            <div class="grow">
                              <div class="font-semibold">
                                {{ t('PICKUP_FROM') }}
                              </div>
                              <div>
                                {{ formattedPickupAddress }}
                              </div>
                            </div>
                            <!--                            <div v-if="currentLocations.length > 1" class="ml-2 grow-0"> -->
                            <!--                              <button class="ml-2 tw-contrast-button" @click="editPickupLocation()"> -->
                            <!--                                <PencilSquareIcon class="h-5 w-5" /> -->
                            <!--                              </button> -->
                            <!--                            </div> -->
                          </div>
                        </div>
                        <div
                          v-else-if="selections.deliveryMethod === DELIVERY_METHODS.DELIVERY"
                          class="w-full mt-5"
                        >
                          <div
                            v-if="selections.deliveryAddress"
                            class="text-tw-1"
                          >
                            <div class="w-full flex flex-row justify-between text-sm">
                              <div
                                v-if="selections.deliveryAddress"
                                class="grow"
                              >
                                <div class="font-semibold">
                                  {{ t('DELIVERY_TO') }}
                                </div>
                                <div>
                                  {{ formattedDeliveryAddress }}
                                </div>
                              </div>
                              <div class="ml-2 grow-0">
                                <button
                                  class="ml-2 tw-contrast-button"
                                  @click="editDeliveryLocation()"
                                >
                                  <PencilSquareIcon class="h-5 w-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                          <div
                            v-else
                            class="w-full"
                          >
                            <button
                              class="text-sm tw-text-button"
                              @click="editDeliveryLocation()"
                            >
                              {{ t('SELECT_DELIVERY_ADDRESS_CONTINUE') }} &rsaquo;
                            </button>
                          </div>
                        </div>
                        <div
                          v-if="days
                            && selections.deliveryMethod
                            && enableTimeSelector
                            && intervals
                            && intervals.length
                          "
                          class="w-full mt-5 relative"
                        >
                          <label class="label-primary mb-2">
                            {{ t('SELECT_X_DAY', { value: selections.deliveryMethod }) }}
                          </label>
                          <div class="w-full text-tw-1">
                            <Listbox
                              v-model="selections.scheduledDay"
                              @update:model-value="handleDayChange"
                            >
                              <ListboxButton
                                class="relative w-full cursor-default rounded-md border border-tw-1 py-2 pl-3 pr-10
                                text-left shadow-md focus:outline-none focus-visible:border-indigo-500
                                focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2
                                focus-visible:ring-offset-orange-300 sm:text-sm"
                              >
                                <span class="block truncate">
                                  {{ t(selections.scheduledDay!.label) }}
                                </span>
                                <span
                                  class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
                                >
                                  <ChevronUpDownIcon
                                    class="h-5 w-5 text-slate-400 dark:text-slate-200"
                                    aria-hidden="true"
                                  />
                                </span>
                              </ListboxButton>
                              <ListboxOptions
                                class="mt-1 max-h-60 w-full overflow-auto rounded-md
                                  bg-tw-2 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5
                                  focus:outline-none sm:text-sm"
                              >
                                <ListboxOption
                                  v-for="(interval) in days"
                                  :key="interval.value"
                                  v-slot="{ active, selected }"
                                  :value="interval"
                                >
                                  <li
                                    :class="[active ? 'bg-blue-500 text-white' : 'text-tw-1']"
                                    class="relative cursor-default select-none py-2 pl-3 pr-9"
                                  >
                                    <span
                                      :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']"
                                    >
                                      {{ interval.label }}
                                    </span>
                                  </li>
                                </ListboxOption>
                              </ListboxOptions>
                            </Listbox>
                          </div>
                        </div>
                        <div
                          v-if="selections.deliveryMethod && enableTimeSelector && intervals && intervals.length"
                          class="w-full mt-5 relative"
                        >
                          <label class="label-primary mb-2">
                            {{ t('SELECT_X_TIME', { value: selections.deliveryMethod }) }}
                          </label>
                          <div class="w-full text-tw-1">
                            <Listbox v-model="selections.scheduledTime">
                              <ListboxButton
                                class="relative w-full cursor-default rounded-md border py-2 pl-3 pr-10
                                text-left shadow-md focus:outline-none focus-visible:border-indigo-500
                                focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2
                                focus-visible:ring-offset-orange-300 sm:text-sm"
                                :class="{
                                  'border-tw-1': validSelectedTime,
                                  'border-rose-600': !validSelectedTime,
                                }"
                              >
                                <span class="block truncate">
                                  {{ scheduledTimeLabel }}
                                </span>
                                <span
                                  class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
                                >
                                  <ChevronUpDownIcon
                                    class="h-5 w-5 text-slate-400 dark:text-slate-200"
                                    aria-hidden="true"
                                  />
                                </span>
                              </ListboxButton>
                              <span
                                v-if="!validSelectedTime"
                                class="label-error mt-1"
                              >
                                Please select a valid time
                              </span>
                              <ListboxOptions
                                class="mt-1 max-h-60 w-full overflow-auto rounded-md
                                  bg-tw-2 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5
                                  focus:outline-none sm:text-sm"
                              >
                                <ListboxOption
                                  v-for="(interval, intervalIdx) in intervals"
                                  :key="interval.value"
                                  v-slot="{ active, selected }"
                                  :value="interval"
                                >
                                  <li
                                    :class="[active ? 'bg-blue-500 text-white' : 'text-tw-1']"
                                    class="relative cursor-default select-none py-2 pl-3 pr-9"
                                  >
                                    <span
                                      :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']"
                                    >
                                      {{ isToday && intervalIdx === 0
                                        ? t('ASAP_DELIVERY_X', { value: currentLocation?.prepTime })
                                        : interval.label
                                      }}
                                    </span>
                                  </li>
                                </ListboxOption>
                              </ListboxOptions>
                            </Listbox>
                          </div>
                        </div>
                        <div
                          v-if="selections.deliveryMethod === DELIVERY_METHODS.PICKUP
                            && currentLocation?.enableCurbsidePickup
                          "
                          class="w-full mt-5"
                        >
                          <div class="relative flex flex-row items-center justify-start">
                            <input
                              id="curbsidePickup"
                              v-model="selections.curbsidePickup"
                              name="curbsidePickup"
                              type="checkbox"
                              class="tw-checkbox"
                            >
                            <label
                              for="curbsidePickup"
                              class="ml-3 block text-sm font-medium leading-6 text-tw-1"
                            >
                              {{ t('CURBSIDE_PICKUP') }}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div class="w-full mt-2 grow-0">
                        <button
                          type="button"
                          :disabled="disableSave()"
                          class="tw-button primary"
                          @click="save()"
                        >
                          {{ t('SAVE') }}
                        </button>
                      </div>
                    </div>
                    <div
                      v-else-if="currentStep === 1"
                      class="flex flex-col place-content-between h-full w-full"
                    >
                      <div class="overflow-y-scroll w-full flex-1">
                        <div
                          v-for="location in currentLocations"
                          :key="location.uuid"
                          class="w-full rounded-md border border-tw-1 text-tw-1 p-5 text-sm mb-4"
                        >
                          <div class="font-semibold">
                            {{ locationName(location) }}
                          </div>
                          <div v-if="location.formattedAddress">
                            {{ location.formattedAddress }}
                          </div>
                          <div v-if="location.phoneFormatted || location.phoneUnformatted">
                            {{ location.phoneFormatted || location.phoneUnformatted }}
                          </div>
                          <button
                            v-if="currentLocations.length > 1"
                            type="button"
                          >
                            {{ t('MAKE_THIS_YOUR_STORE') }}
                          </button>
                        </div>
                      </div>
                      <div class="w-full mt-2 grow-0">
                        <button
                          :disabled="!pickupOptions.locationId"
                          type="button"
                          class="tw-button primary"
                          @click="confirmPickupLocation()"
                        >
                          {{ t('OK') }}
                        </button>
                      </div>
                    </div>
                    <div
                      v-else-if="currentStep === 2"
                      class="flex flex-col place-content-between h-full w-full"
                    >
                      <div class="text-tw-1 overflow-y-scroll w-full">
                        <div class="w-full mb-2">
                          <button
                            type="button"
                            class="text-tw-2 text-sm inline-flex items-center"
                            @click="goBack()"
                          >
                            <ArrowLeftIcon class="h-4 w-4 mr-1" />
                            {{ t('BACK') }}
                          </button>
                        </div>
                        <div
                          :class="{ 'pointer-events-none animate-pulse': deliveryOptions.loading }"
                          class="w-full"
                        >
                          <google-places-auto-complete
                            :placeholder="t('START_TYPING')"
                            :clear-address-on-place-selected="false"
                            @place-changed="handleSelectDeliveryAddress"
                          />
                          <span
                            v-if="error"
                            role="alert"
                            class="label-error mt-1"
                          >
                            {{ error }}
                          </span>
                        </div>
                        <div
                          v-if="deliveryOptions.loading && currentLocations.length"
                          class="w-full mt-2"
                        >
                          <div class="w-full mb-2 rounded-md border border-tw-2 h-20" />
                        </div>
                        <div
                          v-else-if="!deliveryOptions.loading"
                          class="w-full mt-2 text-tw-2"
                        >
                          <div
                            v-if="deliveryOptions.locations.length"
                            class="w-full"
                          >
                            <div
                              v-for="location in deliveryOptions.locations"
                              :key="location.uuid"
                            >
                              // TODO format location
                              {{ location }}
                            </div>
                          </div>
                          <div
                            v-else-if="deliveryOptions.deliveryAddress"
                            class="text-rose-600 font-semibold text-sm"
                          >
                            {{ t('ADDRESS_NOT_DELIVERABLE') }}
                            <button
                              v-if="currentLocations.length === 1"
                              type="button"
                              class="tw-text-button"
                              @click="selectPickup(true)"
                            >
                              {{ t('CHOOSE_PICKUP') }} &rsaquo;
                            </button>
                            <button
                              v-else
                              type="button"
                              class="tw-text-button"
                              @click="selectPickup(false)"
                            >
                              {{ t('CHOOSE_PICKUP') }} &rsaquo;
                            </button>
                          </div>
                        </div>
                        <div
                          v-if="false"
                          class="w-full"
                        >
                          <delivery-address-item
                            v-for="(addressItem, addressItemIdx) in addressItems"
                            :key="addressItem.id"
                            :item="addressItem"
                            :class="{ 'mb-2': !isLast(addressItems, addressItemIdx) }"
                            class="w-full"
                          />
                        </div>
                      </div>
                      <div class="w-full mt-2 grow-0">
                        <button
                          :disabled="disableConfirmDeliveryInformation()"
                          type="button"
                          class="tw-button primary"
                          @click="confirmDeliveryInformation()"
                        >
                          {{ t('OK') }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
